import { TableColumnsType } from "antd";
import {
  Recorder,
  RecorderListDetailResposne,
} from "api/interfaces/recorderInterface.interface";
import { BasePagination } from "api/interfaces/commonInterface.interface";
import { useEffect, useMemo, useState } from "react";
import InfiniteExpandTable from "components/blocks/table/InfiniteExpandTable";
import { useInfiniteQuery } from "react-query";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { getRecorderListDetail } from "api/recorderAPI";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

export default function RecorderListExpand({
  mergedSystemId,
  columns,
  queryInfo,
}: {
  mergedSystemId: string;
  columns: TableColumnsType<Recorder>;
  queryInfo: BasePagination;
}) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [mergedSystemQueryInfo, setMergedSystemQueryInfo] =
    useState<BasePagination>({
      ...baseQueryInfo,
    });
  const recorderDetailQueryKey = useQueryKey(mergedSystemQueryInfo);
  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    [
      "recorderDetail",
      recorderDetailQueryKey,
      mergedSystemId,
      selectedAccount.accountId,
    ],
    ({ pageParam = 0 }) =>
      getRecorderListDetail({
        payload: {
          ...mergedSystemQueryInfo,
          pageNum: pageParam,
        },
        accountId: selectedAccount.accountId,
        mergedSystemId: mergedSystemId,
      }),
    {
      getNextPageParam: (lastPage: RecorderListDetailResposne | undefined) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      enabled: !!mergedSystemId && !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 0,
      onError: (e: any) => {
        setMergedSystemQueryInfo({
          ...baseQueryInfo,
          sortDirection: mergedSystemQueryInfo.sortDirection,
        });
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });
  const mergedColumns = useMemo(() => {
    return columns.map((col) => {
      if (col.key === "type") {
        return {
          ...col,
          render: () => "",
        };
      }
      if (col.title === "Access" || col.key === "firmwareVersion") {
        return {
          ...col,
          hidden: true,
        };
      }

      return col;
    });
  }, [columns]);

  const list = useMemo(() => {
    return (data?.pages || [])
      .flatMap(
        (page: RecorderListDetailResposne) => page.result?.recorders ?? []
      )
      .map((recorder: Recorder, index: number) => {
        return {
          ...recorder,
          cameras: recorder.cameraCount,
          key: recorder.recorderId,
          title: recorder.name,
          type: recorder.type,
          recorderCount: 1,
          version: "",
          recordId: recorder.recorderId,
          mergedSystemId: mergedSystemId,
          enableEdit: data.pages[0]?.result?.enableEdit ?? false,
        };
      });
  }, [data?.pages, mergedSystemId]);

  useEffect(() => {
    setMergedSystemQueryInfo((info) => ({
      ...info,
      sortDirection: queryInfo.sortDirection,
      sortType:
        queryInfo.sortType === "createdAt" ? queryInfo.sortType : "name",
      pageNum: 0,
    }));
  }, [queryInfo.sortDirection, queryInfo.sortType]);

  return (
    <InfiniteExpandTable
      data={list}
      loadMore={fetchNextPage}
      isLoading={isInitialLoading || isFetchingNextPage}
      hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
      columns={mergedColumns as TableColumnsType<Recorder>}
      leftPadding={48}
    />
  );
}
